import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

export const contractorUi = {
  primary: "#2563EB",
  success: "#22C55E",
  background: "#F8FAFC",
  card: "#FFFFFF",
  border: "#E5E7EB",
  text: "#0F172A",
  muted: "#64748B",
  radius: 16,
  shadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
};

export const StatusChip = ({ label, tone = "success" }) => {
  const tones = {
    success: { bg: alpha(contractorUi.success, 0.12), color: "#15803D" },
    warning: { bg: alpha("#F59E0B", 0.14), color: "#B45309" },
    error: { bg: alpha("#EF4444", 0.12), color: "#B91C1C" },
    default: { bg: alpha("#94A3B8", 0.16), color: "#475569" },
    primary: { bg: alpha(contractorUi.primary, 0.12), color: "#1D4ED8" },
  };
  const palette = tones[tone] || tones.default;

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 28,
        fontWeight: 700,
        borderRadius: "999px",
        bgcolor: palette.bg,
        color: palette.color,
        border: "none",
      }}
    />
  );
};

export const SectionHeader = ({ title, subtitle, action }) => (
  <Stack
    direction="row"
    alignItems="flex-start"
    justifyContent="space-between"
    spacing={2}
    sx={{ mb: 2.5 }}
  >
    <Box>
      <Typography
        variant="h6"
        sx={{ fontWeight: 800, color: contractorUi.text, fontSize: "1.05rem" }}
      >
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="body2" sx={{ color: contractorUi.muted, mt: 0.5 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
    {action}
  </Stack>
);

export const InfoCard = ({ title, subtitle, icon, children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      height: "100%",
      borderRadius: `${contractorUi.radius}px`,
      border: `1px solid ${contractorUi.border}`,
      boxShadow: contractorUi.shadow,
      bgcolor: contractorUi.card,
      ...sx,
    }}
  >
    <CardContent sx={{ p: { xs: 2.5, md: 3 }, "&:last-child": { pb: { xs: 2.5, md: 3 } } }}>
      {(title || icon) && (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
          {icon ? (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(contractorUi.primary, 0.1),
                color: contractorUi.primary,
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          ) : null}
          <Box>
            {title ? (
              <Typography sx={{ fontWeight: 800, color: contractorUi.text }}>
                {title}
              </Typography>
            ) : null}
            {subtitle ? (
              <Typography variant="body2" sx={{ color: contractorUi.muted }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      )}
      {children}
    </CardContent>
  </Card>
);

export const InfoField = ({ icon, label, value }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1.25 }}>
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: "10px",
        display: "grid",
        placeItems: "center",
        bgcolor: alpha(contractorUi.primary, 0.08),
        color: contractorUi.primary,
        flexShrink: 0,
        mt: 0.1,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: contractorUi.muted, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          color: contractorUi.text,
          fontWeight: 600,
          fontSize: "0.95rem",
          wordBreak: "break-word",
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  </Stack>
);

export const StatCard = ({ label, value, icon, accent = contractorUi.primary }) => (
  <Card
    elevation={0}
    sx={{
      height: "100%",
      borderRadius: `${contractorUi.radius}px`,
      border: `1px solid ${contractorUi.border}`,
      boxShadow: contractorUi.shadow,
      bgcolor: alpha(accent, 0.08),
    }}
  >
    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" sx={{ color: contractorUi.muted, fontWeight: 600, mb: 1 }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: contractorUi.text }}>
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "12px",
            display: "grid",
            placeItems: "center",
            bgcolor: alpha(accent, 0.16),
            color: accent,
          }}
        >
          {icon || <StarRoundedIcon />}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

export const DocumentRow = ({ name, statusLabel, statusTone = "success", href }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    spacing={2}
    sx={{
      px: 2,
      py: 1.5,
      borderRadius: "12px",
      border: `1px solid ${contractorUi.border}`,
      bgcolor: alpha("#F8FAFC", 0.9),
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "10px",
          display: "grid",
          placeItems: "center",
          bgcolor: alpha(contractorUi.primary, 0.1),
          color: contractorUi.primary,
          flexShrink: 0,
        }}
      >
        <InsertDriveFileOutlinedIcon fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, color: contractorUi.text }} noWrap>
          {name}
        </Typography>
        <Box sx={{ mt: 0.5 }}>
          <StatusChip label={statusLabel} tone={statusTone} />
        </Box>
      </Box>
    </Stack>
    <IconButton
      component={href ? "a" : "button"}
      href={href || undefined}
      target={href ? "_blank" : undefined}
      rel={href ? "noopener noreferrer" : undefined}
      disabled={!href}
      sx={{
        color: contractorUi.primary,
        bgcolor: alpha(contractorUi.primary, 0.08),
        "&:hover": { bgcolor: alpha(contractorUi.primary, 0.14) },
      }}
    >
      <DownloadRoundedIcon fontSize="small" />
    </IconButton>
  </Stack>
);

export const ComplianceItem = ({ label, ok = true }) => (
  <Stack
    direction="row"
    spacing={1.25}
    alignItems="center"
    sx={{
      px: 1.75,
      py: 1.25,
      borderRadius: "12px",
      bgcolor: ok ? alpha(contractorUi.success, 0.08) : alpha("#94A3B8", 0.1),
      border: `1px solid ${ok ? alpha(contractorUi.success, 0.18) : contractorUi.border}`,
    }}
  >
    <CheckCircleRoundedIcon
      sx={{ color: ok ? contractorUi.success : "#94A3B8", fontSize: 22 }}
    />
    <Typography sx={{ fontWeight: 700, color: contractorUi.text, fontSize: "0.92rem" }}>
      {label}
    </Typography>
  </Stack>
);

export const MapPlaceholder = ({ address }) => (
  <Box>
    <Box
      sx={{
        height: 220,
        borderRadius: "14px",
        border: `1px solid ${contractorUi.border}`,
        background: `linear-gradient(135deg, ${alpha(contractorUi.primary, 0.12)}, ${alpha(
          "#0EA5E9",
          0.1
        )}),
        repeating-linear-gradient(0deg, transparent, transparent 24px, ${alpha(
          "#94A3B8",
          0.12
        )} 24px, ${alpha("#94A3B8", 0.12)} 25px),
        repeating-linear-gradient(90deg, transparent, transparent 24px, ${alpha(
          "#94A3B8",
          0.12
        )} 24px, ${alpha("#94A3B8", 0.12)} 25px)`,
        display: "grid",
        placeItems: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          bgcolor: alpha(contractorUi.primary, 0.16),
          display: "grid",
          placeItems: "center",
          color: contractorUi.primary,
          boxShadow: "0 10px 24px rgba(37, 99, 235, 0.2)",
        }}
      >
        <LocationOnOutlinedIcon sx={{ fontSize: 30 }} />
      </Box>
    </Box>
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 2 }}>
      <LocationOnOutlinedIcon sx={{ color: contractorUi.primary, mt: 0.2 }} />
      <Typography sx={{ color: contractorUi.text, fontWeight: 600, lineHeight: 1.8 }}>
        {address || "آدرس ثبت نشده است."}
      </Typography>
    </Stack>
  </Box>
);

export const CompanyAvatar = ({ name }) => (
  <Box
    sx={{
      width: { xs: 64, md: 76 },
      height: { xs: 64, md: 76 },
      borderRadius: "18px",
      bgcolor: alpha(contractorUi.primary, 0.1),
      border: `1px solid ${alpha(contractorUi.primary, 0.16)}`,
      display: "grid",
      placeItems: "center",
      color: contractorUi.primary,
      flexShrink: 0,
    }}
  >
    {name ? (
      <Typography sx={{ fontWeight: 800, fontSize: "1.35rem" }}>
        {String(name).trim().charAt(0)}
      </Typography>
    ) : (
      <BusinessRoundedIcon sx={{ fontSize: 34 }} />
    )}
  </Box>
);
